import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import type { ShopifyProduct } from "@/lib/shopify";
import { formatPrice } from "@/lib/utils";

interface SearchProductGridProps {
  products: ShopifyProduct[];
  textAlign: string;
  onItemClick: () => void;
  /** Optional cap for products to render (e.g. 12 for default state). */
  limit?: number;
}

/** Shared product grid used by every result section in SearchDialog. */
export const SearchProductGrid = ({
  products,
  textAlign,
  onItemClick,
  limit,
}: SearchProductGridProps) => {
  const items = typeof limit === "number" ? products.slice(0, limit) : products;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
      <AnimatePresence mode="popLayout">
        {items.map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, delay: index * 0.03 }}
          >
            <Link
              to={`/products/${product.handle}`}
              onClick={onItemClick}
              className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
            >
              <div className="aspect-square bg-secondary/10 overflow-hidden">
                {product.images?.edges?.[0]?.node ? (
                  <img
                    src={product.images.edges[0].node.url}
                    alt={product.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                  </div>
                )}
              </div>
              <div className={`p-2 sm:p-3 ${textAlign}`}>
                <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                  {product.title}
                </h4>
                <p className="text-primary font-medium text-xs sm:text-sm">
                  {formatPrice(
                    product.priceRange.minVariantPrice.amount,
                    product.priceRange.minVariantPrice.currencyCode
                  )}
                </p>
              </div>
            </Link>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
